import React from 'react';

interface YouTubeBackgroundProps {
  videoUrl: string;
  className?: string;
}

/**
 * Extracts YouTube video ID from various URL formats
 * Supports: youtu.be/ID, youtube.com/watch?v=ID, youtube.com/embed/ID
 */
const extractYouTubeId = (url: string): string | null => {
  const patterns = [
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
};

/**
 * Checks if a URL is a YouTube URL
 */
export const isYouTubeUrl = (url: string): boolean => {
  return url.includes('youtube.com') || url.includes('youtu.be');
};

export const YouTubeBackground: React.FC<YouTubeBackgroundProps> = ({ 
  videoUrl, 
  className = '' 
}) => {
  const videoId = extractYouTubeId(videoUrl);

  if (!videoId) {
    console.warn('Could not extract YouTube video ID from:', videoUrl);
    return null;
  }

  // YouTube embed URL with parameters for background video behavior
  // autoplay=1, mute=1, loop=1, controls=0, showinfo=0, playlist=ID (for looping)
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&modestbranding=1&playlist=${videoId}&playsinline=1&enablejsapi=1`;

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {/* Container that scales video to cover the entire area */}
      <div className="absolute inset-0 w-full h-full">
        <iframe
          src={embedUrl}
          title="Background Video"
          className="absolute top-1/2 left-1/2 w-[300%] h-[300%] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={{
            border: 'none',
            minWidth: '100vw',
            minHeight: '100vh',
          }}
          allow="autoplay; encrypted-media"
          allowFullScreen={false}
        />
      </div>
    </div>
  );
};

export default YouTubeBackground;